import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useNavigate, Routes, Route } from "react-router-dom";
import { Pagination, Input, Dimmer, Loader, Popup, Modal, Button, Dropdown } from "semantic-ui-react";
import { useToasts } from "react-toast-notifications";
import { createPortal } from "react-dom";
import Filters from "app/audit/simple/Filters";
import ContactBreadcrumb from "./ContactBreadcrumb";
import {
  SG_GET_EMPLOYEE_ORG_CATEGORIES,
  SG_GET_EMPLOYEE_ORG_CATEGORIES_BY_FIRST_NAME,
  LOAD_EMPLOYEES,
  SG_GET_SURVEY_STRUCTURES,
  REGISTER_EMPLOYEE,
  SG_VALIDATE_ORG_EMP_EMAIL,
  UPDATE_EMPLOYEE,
  SG_ADD_EMPLOYEE_LOG,
} from "constants/actions";

// Import UserUpload and UserUpdate components
import UserUpload from "./UserUpload";
import UserUpdate from "./UserUpdate";

// Icons
const TeamIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 19 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.7171 10.3764L10.7169 10.3762C10.5416 10.1595 10.5745 9.84114 10.792 9.66537M10.7171 10.3764L11.4276 10.451C12.0498 9.94744 12.8364 9.66987 13.6422 9.66987C15.5721 9.66987 17.1394 11.2212 17.1394 13.1246V14.2869C17.1394 14.317 17.1128 14.3489 17.0713 14.3489H12.6139C12.335 14.3489 12.1085 14.5753 12.1085 14.8542C12.1085 15.1333 12.335 15.3595 12.6139 15.3595H17.0712C17.6653 15.3595 18.1499 14.8793 18.1499 14.2869V13.1246C18.1499 10.6608 16.1265 8.6592 13.6422 8.6592C12.6059 8.6592 11.5939 9.01648 10.792 9.66537M10.7171 10.3764C10.8929 10.5931 11.2109 10.6266 11.4276 10.4511L10.7171 10.3764ZM10.792 9.66537L10.8863 9.78203M10.792 9.66537C10.792 9.66539 10.792 9.66541 10.7919 9.66543L10.8863 9.78203M10.8863 9.78203C10.7334 9.90557 10.7102 10.1294 10.8336 10.2819L10.8863 9.78203Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.3"
    />
    <path
      d="M10.9367 5.75981C10.9367 7.24012 12.1515 8.44191 13.6422 8.44191C15.1329 8.44191 16.3481 7.24033 16.3481 5.75981C16.3481 4.27931 15.1331 3.07754 13.6422 3.07754C12.1514 3.07754 10.9367 4.27951 10.9367 5.75981ZM11.9473 5.75981C11.9473 4.84003 12.706 4.08821 13.6422 4.08821C14.5786 4.08821 15.3374 4.84006 15.3374 5.75981C15.3374 6.67955 14.5788 7.43124 13.6422 7.43124C12.706 7.43124 11.9473 6.67957 11.9473 5.75981Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.3"
    />
    <path
      d="M2.16195 16.7849H11.8073C12.5299 16.7849 13.1193 16.2014 13.1193 15.4814V13.8467C13.1193 10.4954 10.3662 7.77175 6.98463 7.77175C3.60301 7.77175 0.85 10.4954 0.85 13.8467V15.4814C0.85 16.2014 1.4394 16.7849 2.16195 16.7849ZM1.86067 13.8469C1.86067 11.0562 4.15778 8.78259 6.98463 8.78259C9.81148 8.78259 12.1086 11.0562 12.1084 13.8469V15.4816C12.1084 15.6402 11.9758 15.7744 11.8071 15.7744H2.16195C1.99328 15.7744 1.86067 15.6402 1.86067 15.4816V13.8469Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.3"
    />
    <path
      d="M3.42715 4.253C3.42715 6.1312 4.96903 7.65635 6.86121 7.65635C8.75354 7.65635 10.2954 6.13138 10.2954 4.25317C10.2954 2.37515 8.75372 0.85 6.86121 0.85C4.96887 0.85 3.42715 2.37497 3.42715 4.253ZM4.43782 4.25317C4.43782 2.93582 5.52343 1.86067 6.86121 1.86067C8.19916 1.86067 9.28477 2.93583 9.28477 4.25317C9.28477 5.5707 8.19916 6.64586 6.86121 6.64586C5.52344 6.64586 4.43782 5.57071 4.43782 4.25317Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0.3"
    />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
  </svg>
);

const MoreVertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,16A2,2 0 0,1 14,18A2,2 0 0,1 12,20A2,2 0 0,1 10,18A2,2 0 0,1 12,16M12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12A2,2 0 0,1 12,10M12,4A2,2 0 0,1 14,6A2,2 0 0,1 12,8A2,2 0 0,1 10,6A2,2 0 0,1 12,4Z" />
  </svg>
);

const SortIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 21L14 17H17V7H14L18 3L22 7H19V17H22L18 21Z" />
    <path d="M2 19V17H12V19H2Z" />
    <path d="M2 13V11H9V13H2Z" />
    <path d="M2 7V5H6V7H2Z" />
  </svg>
);

const PopoverPortal = ({ children }) => {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
};

// Main wrapper component with routing
const ContactListV2Wrapper = () => {
  return (
    <Routes>
      <Route path="/" element={<ContactListV2Main />} />
      <Route path="/userupload" element={<UserUpload />} />
      <Route path="/userupdate" element={<UserUpdate />} />
    </Routes>
  );
};

/**
 * Builds query parameters for EmployeeCategory API based on selected filters and search text.
 * 
 * @param {Array} selectedFilters - Array of filter objects from state.audit.new_filters
 * @param {string} search - Search text input
 * @param {string} searchField - Which field to search: 'first_name', 'last_name', or null
 * @returns {string} Query string (without leading '?')
 */
const buildCategoryQueryParams = (selectedFilters, search, searchField = 'last_name') => {
  const params = [];

  // Handle text search based on searchField parameter
  if (search && search.trim() && searchField) {
    const searchTerm = search.trim();
    if (searchField === 'first_name') {
      params.push(`employee_first_name_contains=${encodeURIComponent(searchTerm)}`);
    } else if (searchField === 'last_name') {
      params.push(`employee_last_name_contains=${encodeURIComponent(searchTerm)}`);
    }
  }

  // Normalize filters array
  const normalizedFilters = Array.isArray(selectedFilters) ? selectedFilters : [];

  // Process category filters (skip factor/outcome filters)
  const categoryFilters = normalizedFilters.filter(
    (filter) =>
      filter &&
      filter.type_of !== "factor" &&
      filter.type_of !== "outcome"
  );

  // Group filters by type (ID-based vs name/value-based)
  const idBasedFilters = [];
  const nameValueFilters = [];

  categoryFilters.forEach((filter) => {
    // Check for ID-based filter (UUID or numeric ID)
    const filterId =
      filter?.category_value_id ??
      filter?.value_id ??
      filter?.id;

    // Check for name/value-based filter
    const categoryName =
      filter?.category_name ??
      filter?.name;
    const categoryValue =
      filter?.category_value ??
      filter?.value;

    if (filterId !== undefined && filterId !== null && filterId !== "") {
      // ID-based filter
      idBasedFilters.push(String(filterId));
    } else if (categoryName && categoryValue) {
      // Name/value-based filter
      nameValueFilters.push({
        name: categoryName,
        value: categoryValue,
      });
    }
  });

  // Add ID-based filters (if API supports multiple, we'll add them as separate params)
  // For now, if multiple IDs exist, we'll add them as separate category_value_id params
  // Note: The API might need to be checked if it supports arrays or multiple params
  idBasedFilters.forEach((id) => {
    params.push(`category_value_id=${encodeURIComponent(id)}`);
  });

  // Add name/value-based filters
  // If multiple exist, add them as separate params
  nameValueFilters.forEach(({ name, value }) => {
    params.push(`category_name=${encodeURIComponent(name)}`);
    params.push(`category_value=${encodeURIComponent(value)}`);
  });

  return params.join("&");
};

const ContactListV2Main = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const [page, setPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState("1");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const prevPageRef = useRef(undefined);
  const prevDataRef = useRef(undefined);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const prevItemsPerPageRef = useRef(undefined);
  const [categoryOrdering, setCategoryOrdering] = useState("employee__first_name"); // default ascending
  const prevCategoryOrderingRef = useRef(undefined);
  const [memberList, setMemberList] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContact, setNewContact] = useState({
    firstName: "",
    lastName: "",
    email: ""
  });
  const [addingContact, setAddingContact] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [activeMenu, setActiveMenu] = useState({ id: null, anchor: null });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const menuRef = useRef(null);
  const closeActiveMenu = useCallback(
    () => setActiveMenu({ id: null, anchor: null }),
    []
  );

  // Debounce search input to limit API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (!activeMenu.id) {
      return undefined;
    }

    const handleClickAway = (event) => {
      if (event.target.closest("[data-row-menu]")) {
        return;
      }
      closeActiveMenu();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape" || event.key === "Esc") {
        closeActiveMenu();
      }
    };

    document.addEventListener("mousedown", handleClickAway);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickAway);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeMenu, closeActiveMenu]);

  const {
    get_employees,
    get_auth,
    get_employee_org_category,
    get_employee_org_category_by_first_name,
    get_employee_org_category_pagination,
    get_survey_structure,
    core_data,
    categoriesFetched,
    get_email_list,
    selectedFilters,
  } = useSelector(
    (state) => ({
      get_employees: state.employees,
      get_auth: state.auth,
      get_employee_org_category: state.employee_category?.employee_org_category,
      get_employee_org_category_by_first_name: state.employee_category?.employee_org_category_by_first_name,
      get_employee_org_category_pagination: state.employee_category?.employee_org_category_pagination || { count: 0, next: null, previous: null },
      get_survey_structure: state.surveystructure?.survey_structure,
      core_data: state.audit?.core_data,
      categoriesFetched: state.employee_category?.fetched,
      get_email_list: state.employees.allEmailList,
      selectedFilters: state.audit?.new_filters,
    }),
    shallowEqual
  );

  // Load required data on mount and when page, itemsPerPage, or categoryOrdering changes
  useEffect(() => {
    if (get_auth?.organization_id) {
      // Check if page, itemsPerPage, or categoryOrdering changed (not initial load)
      const pageChanged = prevPageRef.current !== page;
      const itemsPerPageChanged = prevItemsPerPageRef.current !== itemsPerPage;
      const orderingChanged = prevCategoryOrderingRef.current !== categoryOrdering;

      // Set loading state if pagination/ordering changed (but not on initial mount)
      if ((pageChanged || itemsPerPageChanged || orderingChanged) && prevPageRef.current !== undefined) {
        setIsLoadingPage(true);
      }

      const filterQuery = buildCategoryQueryParams(selectedFilters, debouncedSearch, 'last_name');
      const baseQuery = [
        `organization=${get_auth.organization_id}`,
        `page_size=${itemsPerPage}`,
        `page=${page}`,
        `ordering=${categoryOrdering}`,
        filterQuery,
      ]
        .filter(Boolean)
        .join("&");

      dispatch({
        type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
        payload: baseQuery,
      });

      // Update refs after dispatch
      prevPageRef.current = page;
      prevItemsPerPageRef.current = itemsPerPage;
      prevCategoryOrderingRef.current = categoryOrdering;

      // Preload org-scoped email list for validation
      if (!get_email_list || !get_email_list.response) {
        dispatch({
          type: SG_VALIDATE_ORG_EMP_EMAIL,
          payload: `organization-id=${get_auth?.organization_id}`,
        });
      }
    }

    if (!get_survey_structure?.[0]) {
      dispatch({
        type: SG_GET_SURVEY_STRUCTURES,
        payload: `organization=${get_auth?.organization_id}`,
      });
    }

    if (get_employees.updated) {
      const timer = setTimeout(() => {
        // Refresh categories instead of employees since we're using categories as source
        const filterQuery = buildCategoryQueryParams(selectedFilters, debouncedSearch, 'last_name');
        const baseQuery = [
          `organization=${get_auth.organization_id}`,
          `page_size=${itemsPerPage}`,
          `page=${page}`,
          `ordering=${categoryOrdering}`,
          filterQuery,
        ]
          .filter(Boolean)
          .join("&");
        dispatch({
          type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
          payload: baseQuery,
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [dispatch, get_auth, get_survey_structure, get_employees.updated, page, itemsPerPage, categoryOrdering, selectedFilters, debouncedSearch]);

  // Listen for successful contact creation
  useEffect(() => {
    if (get_employees.created && get_employees.created.id) {
      // Refresh categories instead of employees since we're using categories as source
      const baseQuery = [
        `organization=${get_auth.organization_id}`,
        `page_size=${itemsPerPage}`,
        `page=${page}`,
        `ordering=${categoryOrdering}`,
      ].join("&");
      dispatch({
        type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
        payload: baseQuery,
      });
    }
  }, [get_employees.created, dispatch, get_auth, itemsPerPage, page, categoryOrdering]);

  // Fetch first name results when search is active
  useEffect(() => {
    if (!get_auth?.organization_id || !debouncedSearch || !debouncedSearch.trim()) {
      return;
    }

    const firstNameQuery = buildCategoryQueryParams(selectedFilters, debouncedSearch, 'first_name');
    const queryParams = [
      `organization=${get_auth.organization_id}`,
      `page_size=${itemsPerPage}`,
      `page=${page}`,
      `ordering=${categoryOrdering}`,
      firstNameQuery,
    ]
      .filter(Boolean)
      .join("&");

    dispatch({
      type: SG_GET_EMPLOYEE_ORG_CATEGORIES_BY_FIRST_NAME,
      payload: queryParams,
    });
  }, [dispatch, get_auth, debouncedSearch, page, itemsPerPage, categoryOrdering, selectedFilters]);

  // Handle email validation response
  useEffect(() => {
    if (!newContact.email.trim()) return;
    const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!emailRegex.test(newContact.email)) return;

    const list = get_email_list?.response || [];
    const exists = Array.isArray(list) && list.some((e) => (e || "").toLowerCase() === newContact.email.trim().toLowerCase());
    setEmailError(exists ? `The email ${newContact.email.trim()} has already been used` : "");
    setValidatingEmail(false);
  }, [get_email_list, newContact.email]);

  // Clear loading state when new data arrives
  useEffect(() => {
    if (isLoadingPage) {
      // Check if data has actually changed (new data arrived)
      const dataChanged = prevDataRef.current !== get_employee_org_category;
      const hasResponse = Array.isArray(get_employee_org_category);

      if (hasResponse && dataChanged) {
        // Clear loading state - new data has arrived
        setIsLoadingPage(false);
        prevDataRef.current = get_employee_org_category;
      }
    } else {
      // Update data ref even when not loading
      prevDataRef.current = get_employee_org_category;
    }
  }, [get_employee_org_category, isLoadingPage]);

  // Get active members from employee_org_category (now includes employee data)
  // When search is active, merge and deduplicate results from both first and last name searches
  const activeMembers = useMemo(() => {
    const transformEntry = (entry) => {
      // Parse employee_full_name into first_name and last_name
      const fullName = (entry?.employee_full_name || "").trim();
      let first_name = "";
      let last_name = "";

      if (fullName) {
        const nameParts = fullName.split(/\s+/);
        if (nameParts.length > 0) {
          first_name = nameParts[0];
          if (nameParts.length > 1) {
            last_name = nameParts.slice(1).join(" ");
          }
        }
      }

      return {
        id: entry?.employee_id || entry?.employee,
        first_name,
        last_name,
        email: entry?.employee_email || "",
        status: entry?.employee_status,
        created_at: entry?.created_at,
        updated_at: entry?.updated_at,
      };
    };

    const filterActive = (entry) => {
      // Filter by employee_status (exclude inactive statuses: 99, 98, 108)
      const status = entry?.employee_status;
      return status !== 99 && status !== 98 && status !== 108;
    };

    // Get last name search results (from Redux)
    const lastNameMembers = Array.isArray(get_employee_org_category)
      ? get_employee_org_category.filter(filterActive).map(transformEntry)
      : [];

    // If no search term, just return last name results
    if (!debouncedSearch || !debouncedSearch.trim()) {
      return lastNameMembers;
    }

    // Get first name search results (from Redux)
    const firstNameMembers = Array.isArray(get_employee_org_category_by_first_name)
      ? get_employee_org_category_by_first_name.filter(filterActive).map(transformEntry)
      : [];

    // Merge and deduplicate by employee ID
    const mergedMap = new Map();

    // Add last name results
    lastNameMembers.forEach(member => {
      mergedMap.set(member.id, member);
    });

    // Add first name results (will not duplicate if ID already exists)
    firstNameMembers.forEach(member => {
      if (!mergedMap.has(member.id)) {
        mergedMap.set(member.id, member);
      }
    });

    return Array.from(mergedMap.values());
  }, [get_employee_org_category, get_employee_org_category_by_first_name, debouncedSearch]);

  // Reset pagination when filters or search text change
  useEffect(() => {
    setPage(1);
  }, [selectedFilters, search]);

  // Get categories for processing using the same approach as Filters.js
  const visibleCategories = useMemo(() => {
    // Try core_data first (like Filters.js), then fallback to survey structure
    const categories = core_data?.categories?.[0]?.categories || get_survey_structure?.[0]?.categories;

    if (!categories) return [];

    // Filter out private categories but be less restrictive on others
    return categories.filter((cat) => {
      // Exclude private categories
      if (cat.visibility === "private") {
        return false;
      }

      return true;
    });
  }, [core_data, get_survey_structure]);


  // Filter and sort members based on search, selected filters, and sorting
  useEffect(() => {
    // Trust API filtering and ordering; just store the current page results
    setMemberList(activeMembers);
  }, [activeMembers]);


  // Get all member's category tags (only from visible/public categories)
  const getMemberTags = (memberId) => {
    const memberCategories = get_employee_org_category?.find(
    (emp) => emp.employee_id === Number(memberId)
  );

  if (!memberCategories?.category) return [];

  const tags = [];

  // Process only categories that exist in visibleCategories
  memberCategories.category.forEach((memberCat) => {
    let tagValue = null;
    let categoryName = null;

    // For primary/secondary priorities, use the structure approach like TableList.js
    if (memberCat?.priority === "primary" || memberCat?.priority === "secondary") {
      // Find the category in the structure by priority
      const structureCategory = visibleCategories.find(
        (cat) => cat.priority === memberCat.priority
      );

      if (structureCategory) {
        categoryName = structureCategory.name;

        // Look for the option by value_id (same as TableList.js getPrimaryCategoriesSafe)
        const option = structureCategory.options?.find(
          (opt) => String(opt?.id) === String(memberCat?.value_id)
        );

        if (option) {
          tagValue = option.name;
        } else {
          // Check subfactors if no direct match
          for (const opt of structureCategory.options || []) {
            if (opt.subfactors?.length > 0) {
              const subfactor = opt.subfactors.find(
                (sf) => String(sf?.id) === String(memberCat?.value_id)
              );
              if (subfactor) {
                tagValue = subfactor.name;
                break;
              }
            }
          }
        }
      }
    } else {
      // For other categories, verify BOTH category AND value exist in visibleCategories
      const structureCategory = visibleCategories.find(
        (cat) => String(cat?.id) === String(memberCat?.name_id) || cat?.name === memberCat?.name
      );

      if (structureCategory && structureCategory?.options) {
        // VALIDATE: Check if the value_id still exists in the category's options
        const option = structureCategory?.options?.find(
          (opt) => String(opt?.id) === String(memberCat?.value_id)
        );

        if (option) {
          // Value exists in current structure
          categoryName = structureCategory?.name;
          tagValue = option?.name;
        }
        // If no matching option found, tag won't be added (value_id doesn't exist anymore)
      }
    }

    // Add the tag if we found both name and value
    if (categoryName && tagValue) {
      tags.push({
        category: categoryName,
        value: tagValue,
        display: `${categoryName}: ${tagValue}`,
        priority: memberCat.priority
      });
    }
  });

  return tags;
};

  // Handle pagination
  const handlePaginationChange = (e, { activePage }) => {
    setPage(activePage);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (field === "name") {
      // Toggle local sort state first
      if (sortField === "name") {
        const nextDirection = sortDirection === "asc" ? "desc" : "asc";
        setSortDirection(nextDirection);
        setCategoryOrdering(
          nextDirection === "asc"
            ? "employee__first_name"
            : "-employee__first_name"
        );
      } else {
        setSortField("name");
        setSortDirection("asc");
        setCategoryOrdering("employee__first_name");
      }
      setPage(1);
      return;
    }
    // Existing behavior for other fields (tags, date)
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(1); // Reset to first page when sorting
  };

  // Get total contacts count from pagination metadata
  const totalContacts = get_employee_org_category_pagination?.count || 0;
  const totalPages = Math.ceil(totalContacts / itemsPerPage);

  // Sync page input value with current page when it changes externally
  useEffect(() => {
    setPageInputValue(page.toString());
  }, [page]);

  // Reset to page 1 when items per page changes
  useEffect(() => {
    setPage(1);
  }, [itemsPerPage]);

  // Handle page jump input
  const handlePageJump = (e) => {
    if (e.key === "Enter") {
      const pageNumber = parseInt(pageInputValue, 10);
      if (
        !isNaN(pageNumber) &&
        pageNumber >= 1 &&
        pageNumber <= totalPages
      ) {
        setPage(pageNumber);
      } else {
        // Reset to current page if invalid
        setPageInputValue(page.toString());
      }
    }
  };

  // Handle blur - reset to current page if invalid
  const handlePageInputBlur = () => {
    const pageNumber = parseInt(pageInputValue, 10);
    if (
      isNaN(pageNumber) ||
      pageNumber < 1 ||
      pageNumber > totalPages
    ) {
      setPageInputValue(page.toString());
    }
  };

  const getMemberDisplayName = (member) => {
    if (!member) return "This contact";
    const name = `${member.first_name || ""} ${member.last_name || ""}`.trim();
    return name || member.email || "This contact";
  };

  const copyEmailToClipboard = async (email, memberName) => {
    if (!email) {
      addToast("No email available to copy.", {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
      } else if (typeof document !== "undefined") {
        const fallbackInput = document.createElement("input");
        fallbackInput.value = email;
        document.body.appendChild(fallbackInput);
        fallbackInput.select();
        document.execCommand("copy");
        document.body.removeChild(fallbackInput);
      } else {
        throw new Error("Clipboard unavailable");
      }

      addToast(`Copied ${memberName}'s email to clipboard.`, {
        appearance: "success",
        autoDismiss: true,
      });
    } catch (err) {
      addToast("Unable to copy email. Please copy it manually.", {
        appearance: "error",
        autoDismiss: true,
      });
    }
  };

  const handleToggleMenu = (event, memberId) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setActiveMenu((prev) =>
      prev.id === memberId
        ? { id: null, anchor: null }
        : { id: memberId, anchor: rect }
    );
  };

  const handleMenuCopyEmail = async (event, member) => {
    event.stopPropagation();
    await copyEmailToClipboard(member?.email, getMemberDisplayName(member));
    closeActiveMenu();
  };

  const handleMenuDeleteContact = (event, member) => {
    event.stopPropagation();
    setMemberToDelete(member);
    setDeleteReason("");
    setShowDeleteModal(true);
    closeActiveMenu();
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
    setDeleteReason("");
  };

  const handleConfirmDelete = () => {
    if (!memberToDelete) return;

    const trimmedReason = deleteReason.trim();
    dispatch({
      type: UPDATE_EMPLOYEE,
      payload: {
        id: memberToDelete.id,
        status: 99,
      },
    });

    dispatch({
      type: SG_ADD_EMPLOYEE_LOG,
      payload: {
        employee: memberToDelete.id,
        type_of_log: 2,
        description: {
          Archived: trimmedReason || "Deleted from contact directory",
        },
      },
    });

    setMemberList((prev) =>
      prev.filter((member) => member.id !== memberToDelete.id)
    );

    addToast(`${getMemberDisplayName(memberToDelete)} deleted.`, {
      appearance: "success",
      autoDismiss: true,
    });

    setShowDeleteModal(false);
    setMemberToDelete(null);
    setDeleteReason("");

    setTimeout(() => {
      dispatch({ type: LOAD_EMPLOYEES });
    }, 400);
  };





  const handleRowClick = (memberId) => {
    navigate(`edit/${memberId}`);
  };

  // Add Contact handlers
  const handleAddContact = () => {
    setShowAddModal(true);
    setNewContact({ firstName: "", lastName: "", email: "" });
    setEmailError("");
    setValidatingEmail(false);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewContact({ firstName: "", lastName: "", email: "" });
    setEmailError("");
    setValidatingEmail(false);
  };

  const handleInputChange = (field, value) => {
    setNewContact(prev => ({
      ...prev,
      [field]: value
    }));

    // Validate email when it changes
    if (field === 'email') {
      setEmailError("");
      if (value.trim()) {
        const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (emailRegex.test(value)) {
          const list = get_email_list?.response || [];
          const exists = Array.isArray(list) && list.some((e) => (e || "").toLowerCase() === value.trim().toLowerCase());
          setEmailError(exists ? `The email ${value.trim()} has already been used` : "");
          setValidatingEmail(false);
        } else {
          setEmailError("Please enter a valid email address");
        }
      }
    }
  };

  const handleSaveContact = async () => {
    // Basic validation
    if (!newContact.email.trim() || emailError || validatingEmail) {
      return;
    }

    setAddingContact(true);
    
    const payload = {
      email: newContact.email.trim(),
      first_name: newContact.firstName.trim() || "",
      last_name: newContact.lastName.trim() || "",
      enable_login: false, // No permissions set
      organization: get_auth?.organization_id,
      role: 11, // Basic role for contacts with no login
      account_type: 9, // Regular user account type
      email_status: 1,
      survey_status: 1, // Allow participation in surveys
      access_data_analytics: 0, // No access to data analytics
      access_subfactor_data: 0, // No access to subfactor data
    };

    dispatch({
      type: REGISTER_EMPLOYEE,
      payload: payload,
    });

    // Close modal and show success
    setShowAddModal(false);
    setAddingContact(false);
    setShowSuccess(true);
    
    // Hide success message after 3 seconds
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);

    // Reset form and errors
    setNewContact({ firstName: "", lastName: "", email: "" });
    setEmailError("");
  };



  // Since we're doing server-side pagination, memberList already contains the correct page's data
  // No need to slice again - the API returns the requested page
  const paginatedMembers = memberList;

  // Loading state
  useEffect(() => {
    if (
      get_employee_org_category?.length > 0 &&
      (core_data?.categories?.[0]?.categories || get_survey_structure?.[0]) &&
      categoriesFetched
    ) {
      setLoading(false);
    } else {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [get_survey_structure, core_data, get_employee_org_category, categoriesFetched]);

  return (
    <Dimmer.Dimmable blurring dimmed={loading}>
      <Container>
        <ContactBreadcrumb />
        {/* Header Section */}
        <HeaderSection>
          <HeaderContent>
            <HeaderLeft>
              <IconWrapper>
                <TeamIcon />
              </IconWrapper>
              <HeaderText>
                <Title>Contact Directory</Title>
                <Subtitle>Manage and view all organization contacts</Subtitle>
              </HeaderText>
            </HeaderLeft>
            <HeaderRight>
              <HeaderButtonSecondary onClick={() => navigate("userupdate")}>
                Update
              </HeaderButtonSecondary>
              <HeaderButtonPrimary onClick={() => navigate("userupload")}>
                Upload
              </HeaderButtonPrimary>
            </HeaderRight>
          </HeaderContent>
        </HeaderSection>

        {/* Members Section */}
        <MembersSection>
          <SectionHeader>
            <SectionLeft>
              <SectionTitle>Contacts ({totalContacts})</SectionTitle>
              <ControlsRow>
                <SearchContainer>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <SearchInput
                    placeholder="Search contacts..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </SearchContainer>
                <FiltersWrapper>
                  <Filters hideFactors />
                </FiltersWrapper>
              </ControlsRow>
            </SectionLeft>
            
            <SectionActions>
              <ActionButton onClick={handleAddContact}>
                <TeamIcon />
                Add Contact
              </ActionButton>
            </SectionActions>
          </SectionHeader>

        <TableContainer>
          <Table>
            <TableHeader>
              <HeaderRow>
                  <SortableTableHeadCell
                    $width="50%"
                    onClick={() => handleSort("name")}
                    $active={sortField === "name"}
                    $direction={sortDirection}
                  >
                    Contact
                    <SortIcon />
                  </SortableTableHeadCell>
                  <SortableTableHeadCell
                    $width="30%"
                    onClick={() => handleSort("tags")}
                    $active={sortField === "tags"}
                    $direction={sortDirection}
                  >
                    Tags
                    <SortIcon />
                  </SortableTableHeadCell>
                  <SortableTableHeadCell
                    $width="80px"
                    onClick={() => handleSort("date")}
                    $active={sortField === "date"}
                    $direction={sortDirection}
                  >
                    Date Added
                    <SortIcon />
                  </SortableTableHeadCell>
                <HeaderCell $width="80px" $alignRight>Actions</HeaderCell>
              </HeaderRow>
            </TableHeader>
            <TableBody>
                {isLoadingPage ? (
                  // Show skeleton rows while loading
                  Array.from({ length: itemsPerPage }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      <Cell $width="50%">
                        <ContactInfo>
                          <SkeletonLine $width="60%" $height="16px" />
                          <SkeletonLine $width="40%" $height="14px" $marginTop="4px" />
                        </ContactInfo>
                      </Cell>
                      <Cell $width="30%">
                        <SkeletonLine $width="50%" $height="14px" />
                      </Cell>
                      <Cell $width="80px">
                        <SkeletonLine $width="70%" $height="14px" />
                      </Cell>
                      <ActionsCell $width="80px">
                        <SkeletonCircle $size="36px" />
                      </ActionsCell>
                    </TableRow>
                  ))
                ) : (
                  paginatedMembers.map((member) => {
                const tags = getMemberTags(member.id);
                const primaryTag = tags[0];
                const remainingTags = tags.slice(1);
                
                return (
                  <TableRow 
                    key={member.id} 
                    onClick={() => handleRowClick(member.id)}
                    $clickable
                  >

                    <Cell $width="50%">
                      <ContactInfo>
                        {(member.first_name || member.last_name) ? (
                          <>
                            <MemberName>
                              {member.first_name} {member.last_name}
                            </MemberName>
                            <Email>{member.email || "No email"}</Email>
                          </>
                        ) : (
                          <MemberName>{member.email || "N/A"}</MemberName>
                        )}
                      </ContactInfo>
                    </Cell>
                    <Cell $width="30%">
                      <TagsContainer>
                        {tags.length > 0 ? (
                          <>
                            <PrimaryTag>{primaryTag.display}</PrimaryTag>
                            {remainingTags.length > 0 && (
                              <Popup
                                content={
                                  <TagsPopup>
                                    <PopupTitle>All Tags</PopupTitle>
                                    <TagsGrid>
                                      {tags.map((tag, index) => (
                                        <PopupTag key={index}>{tag.display}</PopupTag>
                                      ))}
                                    </TagsGrid>
                                  </TagsPopup>
                                }
                                trigger={
                                  <MoreTags>+{remainingTags.length} more</MoreTags>
                                }
                                position="top center"
                                hoverable
                              />
                            )}
                          </>
                        ) : (
                          <NoTags>No tags</NoTags>
                        )}
                      </TagsContainer>
                    </Cell>
                    <Cell $width="80px">
                      <DateAdded>
                        {new Date(member.created_at || member.updated_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </DateAdded>
                    </Cell>
                    <ActionsCell
                      $width="80px"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RowActionMenuWrapper data-row-menu>
                        <RowMenuButton
                          type="button"
                          data-row-menu
                          aria-haspopup="menu"
                          aria-expanded={activeMenu.id === member.id}
                          aria-label={`More actions for ${getMemberDisplayName(member)}`}
                          onClick={(event) => handleToggleMenu(event, member.id)}
                        >
                          <MoreVertIcon />
                        </RowMenuButton>
                        {activeMenu.id === member.id && activeMenu.anchor && (
                          <PopoverPortal>
                            <RowFloatingMenu
                              ref={menuRef}
                              data-row-menu
                              style={{
                                position: "fixed",
                                top: activeMenu.anchor.bottom + 4,
                                left: activeMenu.anchor.right,
                                transform: "translateX(-100%)",
                              }}
                            >
                              <RowMenuItem
                                type="button"
                                onClick={(event) => handleMenuCopyEmail(event, member)}
                                disabled={!member?.email}
                              >
                                Copy Email
                              </RowMenuItem>
                              <RowMenuDivider />
                              <RowMenuItem
                                type="button"
                                danger
                                onClick={(event) => handleMenuDeleteContact(event, member)}
                              >
                                Delete Contact
                              </RowMenuItem>
                            </RowFloatingMenu>
                          </PopoverPortal>
                        )}
                      </RowActionMenuWrapper>
                    </ActionsCell>
                  </TableRow>
                );
                  })
                )}
            </TableBody>
          </Table>
        </TableContainer>

        {memberList.length === 0 && !loading && (
          <EmptyMembersState>
            <EmptyIcon>
              <TeamIcon />
            </EmptyIcon>
            <EmptyTitle>No Contacts Found</EmptyTitle>
            <EmptyText>
              {search ? `No contacts found matching "${search}"` : "No contacts have been added yet."}
            </EmptyText>
          </EmptyMembersState>
        )}

        {/* Pagination */}
          {totalContacts > 0 && (
          <PaginationSection>
            <PaginationInfo>
                Showing {Math.min((page - 1) * itemsPerPage + 1, totalContacts)} to {Math.min(page * itemsPerPage, totalContacts)} of {totalContacts} contacts
            </PaginationInfo>
            <PaginationControls>
                <ItemsPerPageContainer>
                  <ItemsPerPageLabel>Show:</ItemsPerPageLabel>
                  <Dropdown
                    selection
                    compact
                    value={itemsPerPage}
                    onChange={(e, { value }) => setItemsPerPage(value)}
                    options={[
                      { key: 10, text: '10', value: 10 },
                      { key: 50, text: '50', value: 50 },
                      { key: 100, text: '100', value: 100 },
                    ]}
                  />
                </ItemsPerPageContainer>
                {totalPages > 1 && (
                  <>
              <PaginationButton
                onClick={() => setPage(prev => Math.max(1, prev - 1))}
                disabled={page === 1}
              >
                Previous
              </PaginationButton>
                <PaginationInfo>
                  Page{" "}
                  <PageJumpInput
                    type="number"
                    min="1"
                        max={totalPages}
                    value={pageInputValue}
                    onChange={(e) => setPageInputValue(e.target.value)}
                    onKeyDown={handlePageJump}
                    onBlur={handlePageInputBlur}
                  />{" "}
                      of {totalPages}
                </PaginationInfo>
              <PaginationButton
                      onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages}
              >
                Next
              </PaginationButton>
                  </>
                )}
            </PaginationControls>
          </PaginationSection>
        )}
        
        </MembersSection>
      </Container>

      {/* Add Contact Modal */}
      <Modal
        open={showAddModal}
        onClose={handleCloseModal}
        size="small"
      >
        <Modal.Header>Add New Contact</Modal.Header>
        <Modal.Content>
          <ModalForm>
            <InputGroup>
              <Label>First Name (Optional)</Label>
              <Input
                type="text"
                placeholder="Enter first name"
                value={newContact.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Last Name (Optional)</Label>
              <Input
                type="text"
                placeholder="Enter last name"
                value={newContact.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>Email Address *</Label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={newContact.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!emailError}
                loading={validatingEmail}
              />
              {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
            </InputGroup>
          </ModalForm>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button 
            primary 
            onClick={handleSaveContact}
            disabled={!newContact.email.trim() || addingContact || emailError || validatingEmail}
            loading={addingContact}
          >
            Add Contact
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Success Message */}
      {showSuccess && (
        <SuccessMessage>
          <SuccessIcon>✓</SuccessIcon>
          Contact added successfully!
        </SuccessMessage>
      )}

      <Dimmer active={loading}>
        <LoadingContainer>
          <p>Loading contacts...</p>
          <Loader size="medium">Loading</Loader>
        </LoadingContainer>
      </Dimmer>

      <Modal
        open={showDeleteModal}
        onClose={handleCancelDelete}
        size="tiny"
        closeOnDimmerClick={false}
      >
        <Modal.Header>Delete Contact</Modal.Header>
        <Modal.Content>
          <ConfirmModalText>
            Deleting {getMemberDisplayName(memberToDelete)} will move them to the inactive list and revoke their access. You can optionally add a reason that will be recorded in the audit log.
          </ConfirmModalText>
          <ReasonLabel htmlFor="delete-reason">Reason (optional)</ReasonLabel>
          <ReasonTextArea
            id="delete-reason"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            rows={3}
            placeholder="Add a note for the audit log"
          />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button negative onClick={handleConfirmDelete}>
            Delete Contact
          </Button>
        </Modal.Actions>
      </Modal>
    </Dimmer.Dimmable>
  );
};

export default ContactListV2Wrapper;

// Styled Components
const Container = styled.div`
  background: white;
  min-height: 100vh;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderButtonPrimary = styled.button`
  background: transparent;
  color: #476dfa;
  border: 1px solid #476dfa;
  border-radius: 6px;
  padding: 10px 20px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #3d5ce6;
    border-color: #3d5ce6;
  }

  &:active {
    color: #2a4bc7;
    border-color: #2a4bc7;
  }
`;

const HeaderButtonSecondary = styled.button`
  background: transparent;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 20px;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #374151;
    border-color: #9ca3af;
  }

  &:active {
    color: #1f2937;
    border-color: #6b7280;
  }
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #476dfa 0%, #3d5ce6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(71, 109, 250, 0.3);
`;

const HeaderText = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #1f2937;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const MembersSection = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-bottom: 1px solid #e5e7eb;
  gap: 24px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const SectionLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #1f2937;
  margin: 0;
  white-space: nowrap;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  min-width: 0;

  & > div {
    width: 100%;
    max-width: 100%;
  }

  @media (max-width: 1024px) {
    justify-content: flex-start;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  min-width: 320px;

  @media (max-width: 768px) {
    min-width: auto;
    width: 100%;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  background: #fafafa;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #476dfa;
    background: white;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;




const ActionButton = styled.button`
  background: ${props => props.danger ? '#ef4444' : '#f8f9fa'};
  color: ${props => props.danger ? 'white' : '#374151'};
  border: 1px solid ${props => props.danger ? '#ef4444' : '#d1d5db'};
  border-radius: 8px;
  padding: 8px 16px;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${props => props.danger ? '#dc2626' : '#f3f4f6'};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TableContainer = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  overflow-x: auto;
  margin-bottom: 20px;
`;

const Table = styled.div`
  width: max-content;
  min-width: 100%;
`;

const TableHeader = styled.div`
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: stretch;
`;

const HeaderCell = styled.div`
  padding: 12px 16px;
  font-weight: 500;
  font-size: 13px;
  color: #6c757d;
  text-align: left;
  width: ${props => props.$width};
  min-width: ${props => props.$width};
  max-width: ${props => props.$width};
  border-right: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: ${props => (props.$alignRight ? "flex-end" : "flex-start")};

  &:last-child {
    border-right: none;
  }
`;

const SortableTableHeadCell = styled.div`
  padding: 12px 16px;
  font-weight: 500;
  font-size: 13px;
  color: #6c757d;
  text-align: left;
  width: ${props => props.$width};
  min-width: ${props => props.$width};
  max-width: ${props => props.$width};
  border-right: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: flex-start;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;

  &:hover {
    background: #f0f0f0;
  }

  svg {
    margin-left: 4px;
    opacity: ${(props) => (props.$active ? 1 : 0.3)};
    transform: ${(props) =>
    props.$active && props.$direction === "desc"
      ? "rotate(180deg)"
      : "rotate(0deg)"};
    transition: all 0.2s;
  }

  ${(props) =>
    props.$active &&
    `
    background: #f0f4ff;
    color: #476dfa;
    font-weight: 600;
  `}

  &:last-child {
    border-right: none;
  }
`;

const TableBody = styled.div`
  /* Body styles */
`;

const TableRow = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #f1f3f4;
  transition: all 0.2s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    background-color: ${props => props.$clickable ? '#f0f7ff' : '#f8f9fa'};
    ${props => props.$clickable && 'transform: translateY(-1px);'}
    ${props => props.$clickable && 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);'}
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  color: #495057;
  border-right: 1px solid #f1f3f4;
  width: ${props => props.$width};
  min-width: ${props => props.$width};
  max-width: ${props => props.$width};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: ${props => (props.$alignRight ? "flex-end" : "flex-start")};
  word-wrap: break-word;
  overflow-wrap: break-word;

  &:last-child {
    border-right: none;
  }
`;

const ActionsCell = styled(Cell)`
  justify-content: flex-end;
`;

// Skeleton loading components
const SkeletonLine = styled.div`
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 4px;
  width: ${props => props.$width || "100%"};
  height: ${props => props.$height || "16px"};
  margin-top: ${props => props.$marginTop || "0"};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const SkeletonCircle = styled.div`
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 50%;
  width: ${props => props.$size || "36px"};
  height: ${props => props.$size || "36px"};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const RowActionMenuWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const RowMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  background: white;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
    color: #1f2937;
  }

  &:focus-visible {
    outline: 2px solid #476dfa;
    outline-offset: 2px;
  }
`;

const RowFloatingMenu = styled.div`
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  padding: 6px 0;
  z-index: 1000;
`;

const RowMenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.danger ? "#dc2626" : "#374151")};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;

  &:not(:disabled):hover {
    background: ${(props) => (props.danger ? "#fff5f5" : "#f3f4f6")};
  }

  &:disabled {
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const RowMenuDivider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
`;



const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemberName = styled.div`
  font-weight: 500;
  color: #212529;
  line-height: 1.3;
`;

const Email = styled.div`
  color: #6c757d;
  font-size: 13px;
  word-break: break-word;
  line-height: 1.3;
`;

const TagsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PrimaryTag = styled.div`
  color: #495057;
  font-size: 13px;
`;

const MoreTags = styled.div`
  color: #6c757d;
  font-size: 13px;
  cursor: pointer;
  
  &:hover {
    color: #495057;
  }
`;

const NoTags = styled.div`
  color: #adb5bd;
  font-size: 13px;
  font-style: italic;
`;

const TagsPopup = styled.div`
  padding: 16px;
  max-width: 450px;
  min-width: 300px;
`;

const PopupTitle = styled.div`
  font-weight: 600;
  margin-bottom: 12px;
  color: #212529;
  font-size: 14px;
`;

const TagsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
`;

const PopupTag = styled.div`
  padding: 6px 12px;
  background: transparent;
  border: 1px solid #e9ecef;
  border-radius: 16px;
  color: #495057;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.2s ease;
  
  &:hover {
    color: #374151;
    border-color: #d1d5db;
  }
`;

const DateAdded = styled.div`
  color: #6c757d;
  font-size: 13px;
`;

const EmptyMembersState = styled.div`
  text-align: center;
  padding: 80px 32px;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 16px;
  border: 2px dashed #d1d5db;
  margin: 32px;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.6;
  color: #9ca3af;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins", sans-serif;
  font-weight: 400;
  font-size: 15px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
`;

const PaginationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px;
  border-top: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const PaginationInfo = styled.div`
  display: inline-flex;
  align-items: center;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #6b7280;
  gap: 4px;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ItemsPerPageLabel = styled.span`
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
`;

const PaginationButton = styled.button`
  background: transparent;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: #1f2937;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const PageNumber = styled.div`
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
`;

const PageJumpInput = styled.input`
  width: 50px;
  padding: 4px 6px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins", sans-serif;
  font-weight: 400;
  font-size: 13px;
  color: #414141;
  text-align: center;
  outline: none;
  transition: border-color 0.2s;
  display: inline-block;

  &:focus {
    border-color: #476dfa;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const LoadingContainer = styled.div`
  min-height: 40vh;
  padding: 30px;
  text-align: center;

  p {
    margin-bottom: 20px;
    color: #6c757d;
    font-family: "Poppins", sans-serif;
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

// Modal Styled Components
const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 0;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #374151;
`;

const ErrorMessage = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
`;

const ConfirmModalText = styled.p`
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const ReasonLabel = styled.label`
  display: block;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: #374151;
  margin-bottom: 6px;
`;

const ReasonTextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #1f2937;
  resize: vertical;
  min-height: 90px;

  &:focus {
    outline: none;
    border-color: #476dfa;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.15);
  }
`;

// Success Message Components
const SuccessMessage = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: #10b981;
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "Poppins", sans-serif;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const SuccessIcon = styled.span`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;
